import {
  PRODUCT_FLAG_ROWS,
  type ProductFlagFormValues,
  type ProductFlagTier,
  type SettableProductFlagField,
  TIER_SECTION_TITLES,
} from '@/definitions/productFlags'

import { isRequirementMet, requirementOf } from './flagDependencies'
import { isTierEditable } from './tierEditability'

export interface FlagRowState {
  label: string
  field: SettableProductFlagField
  value: boolean
  /** False while a lower package is selected, which locks the row. */
  isEditable: boolean
  /** The product flag this one sits behind, named as its own switch names it. */
  requirementLabel?: string
  /** False while the product flag it sits behind is off, which is what keeps it off. */
  isRequirementMet: boolean
  /** Why the last save did not apply this flag, when it did not. */
  errorMessage?: string
  /** The one row that also carries an account SID. */
  isTwilioAccount: boolean
  /**
   * The one row whose switch, turned off, destroys something the practice cannot
   * get back — its Pocketflows business and every email template in it.
   */
  isPocketflowsBusiness: boolean
}

export interface FlagSection {
  tier: ProductFlagTier
  title: string
  /** False when a lower package is selected, which greys the whole section. */
  isEditable: boolean
  rows: FlagRowState[]
}

const SECTION_ORDER: readonly ProductFlagTier[] = ['manual', 'core', 'premium', 'copilot']

/**
 * Pairs every product flag with its current value, whether it can be changed, what it
 * sits behind, and anything the last save had to say about it.
 *
 * Values are reported as they are, never corrected: an organization stored with a
 * product flag on whose requirement is off shows exactly that, with the requirement
 * named, rather than being quietly rewritten by opening the tab.
 *
 * `storedAccountSid` is what the organization has on record, which is not always what
 * the SID field holds — an emptied field cannot clear it — so Texting is judged against
 * the subaccount that would actually be reached.
 */
export const buildFlagSections = (
  values: ProductFlagFormValues,
  selectedTier: ProductFlagTier | null,
  storedAccountSid: string | null,
  errorsByField: Record<string, string> = {}
): FlagSection[] =>
  SECTION_ORDER.map((tier) => ({
    tier,
    title: TIER_SECTION_TITLES[tier],
    isEditable: isTierEditable(tier, selectedTier),
    rows: PRODUCT_FLAG_ROWS.filter((row) => row.tier === tier).map((row) => {
      const requirement = requirementOf(row.field)

      return {
        label: row.label,
        field: row.field,
        value: values[row.field],
        isEditable: isTierEditable(row.tier, selectedTier),
        ...(requirement !== null ? { requirementLabel: requirement.label } : {}),
        isRequirementMet: isRequirementMet(values, row.field, storedAccountSid),
        ...(errorsByField[row.field] !== undefined
          ? { errorMessage: errorsByField[row.field] }
          : {}),
        isTwilioAccount: row.field === 'twilio_enabled',
        isPocketflowsBusiness: row.field === 'pocketflows_enabled',
      }
    }),
  }))
